function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

/** @jsxRuntime classic */

/** @jsx jsx */
import { jsx, css } from '@emotion/core';
import { TooltipPopup, useTooltip } from '@reach/tooltip';
import Portal from '@reach/portal';
import React from 'react';
import '@reach/tooltip/styles.css';
import { useTheme } from 'emotion-theming';
import { home, migratoryWebCitizen, phytosanitaryWeb, sanitarianRegistersWeb, tdCarWeb } from './Colors';
const themes = {
  home: {
    alpha: home.white,
    beta: home.cerulean,
    gamma: home.cerulean
  },
  migratoryWebCitizen: {
    alpha: migratoryWebCitizen.white,
    beta: migratoryWebCitizen.cerulean,
    gamma: migratoryWebCitizen.cerulean
  },
  sanitarianRegistersWeb: {
    alpha: sanitarianRegistersWeb.white,
    beta: sanitarianRegistersWeb.deepCerulean,
    gamma: sanitarianRegistersWeb.deepCerulean
  },
  phytosanitaryWeb: {
    alpha: phytosanitaryWeb.white,
    beta: phytosanitaryWeb.cerulean,
    gamma: phytosanitaryWeb.cerulean
  },
  tdCarWeb: {
    alpha: tdCarWeb.white,
    beta: tdCarWeb.cerulean,
    gamma: tdCarWeb.cerulean
  }
};

function getTextWidth(text, font) {
  const canvas = getTextWidth.canvas || (getTextWidth.canvas = document.createElement('canvas'));
  const context = canvas.getContext('2d');
  context.font = font;
  const metrics = context.measureText(text);
  return metrics.width;
}

function positionTooltip({
  position,
  triggerRect,
  tooltipRect,
  shouldAutoWidth,
  label
}) {
  let share = {};

  switch (position) {
    case 'top-right':
      share = {
        left: triggerRect.left - triggerRect.width / 2,
        top: triggerRect.top - tooltipRect.height - 10 + window.scrollY
      };
      break;

    case 'top-center':
      {
        const triggerCenter = triggerRect.left + triggerRect.width / 2;
        const left = triggerCenter - tooltipRect.width / 2;
        const maxLeft = window.innerWidth - tooltipRect.width - 2;
        share = {
          left: Math.min(Math.max(2, left), maxLeft) + window.scrollX,
          top: triggerRect.top - tooltipRect.height - 10 + window.scrollY
        };
      }
      break;

    case 'top-left':
      {
        const left = triggerRect.left - (getTextWidth(label, 'Rubik') + triggerRect.width / 2);
        const maxLeft = window.innerWidth - tooltipRect.width - 2;
        share = {
          left: Math.min(Math.max(2, left), maxLeft) + window.scrollX,
          top: triggerRect.top - tooltipRect.height - 10 + window.scrollY
        };
      }
      break;

    case 'bottom-right':
      share = {
        left: triggerRect.left - triggerRect.width / 2,
        top: triggerRect.bottom + 10 + window.scrollY
      };
      break;

    case 'bottom-center':
      {
        const triggerCenter = triggerRect.left + triggerRect.width / 2;
        const left = triggerCenter - tooltipRect.width / 2;
        const maxLeft = window.innerWidth - tooltipRect.width - 2;
        share = {
          left: Math.min(Math.max(2, left), maxLeft) + window.scrollX,
          top: triggerRect.bottom + 10 + window.scrollY
        };
      }
      break;

    case 'bottom-left':
      {
        const left = triggerRect.left - (getTextWidth(label, 'Rubik') + triggerRect.width / 2);
        const maxLeft = window.innerWidth - tooltipRect.width - 2;
        share = {
          left: Math.min(Math.max(2, left), maxLeft) + window.scrollX,
          top: triggerRect.bottom + 10 + window.scrollY
        };
      }
      break;

    default:
      break;
  }

  if (shouldAutoWidth) {
    return { ...share,
      width: triggerRect.width,
      maxWidth: triggerRect.width
    };
  }

  return share;
}
/**
 * Este es un componente tipo Tooltip que recibe las siguientes props
 * @param {*} children Prop que permite envolver los componentes dentro de las boquillas
 * @param {*} label Prop que permite agregar el texto dentro del Tooltip
 * @param {*} ariaLabel Prop que permite definir una cadena que etiqueta el elemento Tooltip
 * @param {*} position Prop que que permite setear la posición, top-right, top-center, top-left, bottom-right, bottom-center, bottom-left
 * @param {*} isVisible Prop que permite que el tooltip sea visible
 */


export default function Tooltip({
  children,
  label,
  'aria-label': ariaLabel,
  position = 'top',
  isVisible: isVisibleControlled = true,
  shouldAutoWidth = true
}) {
  const [trigger, tooltip] = useTooltip();
  const {
    isVisible,
    triggerRect: tempTriggerRect
  } = tooltip;
  return jsx(React.Fragment, null, /*#__PURE__*/React.cloneElement(children, trigger), isVisible && isVisibleControlled && jsx(React.Fragment, null, jsx(Portal, null, jsx("div", {
    style: portalStyle(position, tempTriggerRect)
  })), jsx(TooltipPopup, _extends({}, tooltip, {
    label: label,
    "aria-label": ariaLabel,
    css: tooltipStyle(position),
    position: (triggerRect, tooltipRect) => positionTooltip({
      position,
      triggerRect,
      tooltipRect,
      shouldAutoWidth,
      label
    }),
    isVisible: true
  }))));
}

const tooltipStyle = () => {
  const {
    theme = 'migratoryWebCitizen'
  } = useTheme();
  return css`
    font-family: Rubik;
    font-style: normal;
    font-weight: normal;
    font-size: 13px !important;
    color: ${themes[theme].alpha} !important;
    background-color: ${themes[theme].beta} !important;
    border-radius: 4px !important;
    padding: 4px 8px !important;
    box-sizing: border-box !important;
    overflow-wrap: break-word !important;
    word-wrap: break-word !important;
    hyphens: auto !important;
    white-space: unset !important;
    border: none !important;
  `;
};

const portalStyle = (position, triggerRect) => {
  const {
    theme = 'migratoryWebCitizen'
  } = useTheme();
  let border = {};
  const borderColor = themes[theme].gamma;

  switch (position) {
    case 'top-right':
    case 'top-center':
    case 'top-left':
      border = {
        borderTop: `8px solid ${borderColor}`,
        top: triggerRect && triggerRect.top + window.scrollY - 10
      };
      break;

    case 'bottom-right':
    case 'bottom-center':
    case 'bottom-left':
      border = {
        borderBottom: `8px solid ${borderColor}`,
        top: triggerRect && triggerRect.bottom + 2 + window.scrollY
      };
      break;

    default:
      break;
  }

  return {
    position: 'absolute',
    left: triggerRect && triggerRect.left - 8 + triggerRect.width / 2,
    width: 0,
    height: 0,
    borderLeft: '8px solid transparent',
    borderRight: '8px solid transparent',
    ...border
  };
};